import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import ListLoader from "../../utils/loader/ListLoader";
import {Button, ButtonStyles} from "../form/Button/Button";
import window from "../WindowExtended";
import Modal from "./Modal";
import {useCheckoutContext} from "../../checkout/CheckoutContextProvider/CheckoutContextProvider";
import {useCustomerSelector} from "../../../store";
import {validateEmail} from "../../../helpers/emailRegExp";

interface IMagicLinkModalProps {
  givenEmail: string | null;
  closeFunction: () => void;
  isVisible: boolean;
}
const MagicLinkModal: React.FC<IMagicLinkModalProps> = (props) => {
  const {t} = useTranslation("checkout");
  const {customer} = useCustomerSelector();
  const [{emailSent, error, sendingEmail, emailValue}, setEmailState] = useState<{
    emailSent: boolean;
    emailValue: string;
    error: boolean;
    sendingEmail: boolean;
  }>({
    emailSent: false,
    emailValue: "",
    error: false,
    sendingEmail: false,
  });

  // region preparation of the redirect url

  let areWeIntoCheckout: boolean;
  try {
    /**
     * when checkout context present, then we are into checkout process
     * and redirect url should lead again to react checkout process
     * (redirect url is called in magic link mail)
     */
    areWeIntoCheckout = !!useCheckoutContext().checkout;
  } catch (e) {
    /**
     * when we are not below CheckoutContextProvider, the
     * useCheckoutContext() method will throw an error
     * what implies we are not into checkout
     */
    areWeIntoCheckout = false;
  }
  const {origin, pathname, search} = window.location;
  const usp = new URLSearchParams(search);
  // if into checkout, redirect url needs gotoStep param, else, no param needed
  if (areWeIntoCheckout) {
    usp.set("gotoStep", "magicLink");
  }
  const redirectUrl = origin + pathname + (usp.toString() ? "?" + usp.toString() : "");

  // endregion preparation of the redirect url

  const resetter = () => {
    setEmailState({emailSent: false, error: false, sendingEmail: false, emailValue: ""});
    props.closeFunction();
  };

  if (sendingEmail) {
    return (
      <Modal isVisible={props.isVisible} closeFunction={props.closeFunction}>
        <br />
        <ListLoader />
      </Modal>
    );
  }
  const EmailSent: React.FC = () => (
    <React.Fragment>
      <h2>{t("form.checkInbox")}</h2>
      <p className="content">
        {t("general.emailWithLoginLink--email", {
          email: emailValue,
        })}
      </p>
      <div className="center">
        <Button styles={[ButtonStyles.big]} action={resetter}>
          {t("general.okay")}
        </Button>
      </div>
    </React.Fragment>
  );

  const EnterEmail: React.FC = () => {
    const [email, setEmail] = useState<string>(props.givenEmail || "");
    const [validEmail, setValidEmail] = useState<boolean>(true);

    const sendEmail = async (evt: React.FormEvent) => {
      evt.preventDefault();
      try {
        if (email && validateEmail(email)) {
          setValidEmail(true);
          setEmailState((prevState) => ({...prevState, sendingEmail: true}));
          await customer.requestMagicLink(email, redirectUrl);
          setEmailState({emailSent: true, error: false, sendingEmail: false, emailValue: email});
        } else {
          setValidEmail(false);
        }
      } catch (e) {
        setEmailState({emailSent: false, error: true, sendingEmail: false, emailValue: ""});
      }
    };

    if (emailSent) {
      return <EmailSent />;
    }

    return (
      <React.Fragment>
        <h2>{t("general.alreadyACustomer")}</h2>
        <p className="content">{t("general.skipDataEntry")}</p>
        {error && <p style={{color: "red"}}>{t("general.emailNetworkError")}</p>}
        {!validEmail && <span style={{color: "red"}}>{t("form.emailNotValid")}</span>}
        <form style={{margin: "16px 0"}} onSubmit={(evt: React.FormEvent) => sendEmail(evt)}>
          <input
            style={{marginBottom: "16px", display: "block"}}
            type="email"
            defaultValue={props.givenEmail ?? ""}
            placeholder={t("form.yourEmailAddress") + " **"}
            onChange={(e) => setEmail(e.target.value.trim())}
          />
          <Button styles={[ButtonStyles.big]}>{t("general.generateLoginLink")}</Button>
        </form>
      </React.Fragment>
    );
  };

  return (
    <Modal isVisible={props.isVisible} closeFunction={resetter}>
      <EnterEmail />
    </Modal>
  );
};

export default MagicLinkModal;
